/**
 * Data for the "Descargar datos" modal, read from Supabase through the proxy's
 * /inbox/export/* endpoints (cloud_functions/airtable_proxy/inbox_data.py).
 * Replaces the Airtable reads that stopped working when the Airtable tokens
 * were revoked on 2026-10-08. Account scope is enforced server-side from the
 * login token, like every other /inbox/* call.
 */
import { addDays, startOfDay } from 'date-fns';
import { inboxRequest } from './dataSource/supabaseProvider';
import { supabaseRowToExportMeeting, supabaseLeadToExportRecord } from './dataSource/fieldMapper';
import { SupabaseExportMeeting, SupabaseExportLead } from './dataSource/types';

type ExportRecord = { id: string; fields: Record<string, unknown> };

/** [start of startDate, start of the day after endDate) in the viewer's local time. */
function toRange(startDate: Date, endDate: Date | null): { from: string; to: string } {
  return {
    from: startOfDay(startDate).toISOString(),
    // null = open-ended, so "all time" still includes meetings booked ahead.
    to: endDate ? addDays(startOfDay(endDate), 1).toISOString() : '9999-12-31T00:00:00.000Z',
  };
}

export async function countMeetingsForExport(accountName: string, startDate: Date, endDate: Date | null): Promise<number> {
  if (!accountName) return 0;
  const { count } = await inboxRequest<{ count: number }>(
    '/inbox/export/meetings',
    { accountName, ...toRange(startDate, endDate), countOnly: true },
  );
  return count ?? 0;
}

export async function fetchMeetingsForExport(accountName: string, startDate: Date, endDate: Date | null): Promise<ExportRecord[]> {
  if (!accountName) return [];
  const { rows } = await inboxRequest<{ rows: SupabaseExportMeeting[] }>(
    '/inbox/export/meetings',
    { accountName, ...toRange(startDate, endDate) },
  );
  return (rows || []).map(supabaseRowToExportMeeting);
}

export async function countInboxForExport(accountName: string): Promise<number> {
  if (!accountName) return 0;
  const { count } = await inboxRequest<{ count: number }>(
    '/inbox/export/leads',
    { accountName, countOnly: true },
  );
  return count ?? 0;
}

export async function fetchInboxForExport(accountName: string): Promise<ExportRecord[]> {
  if (!accountName) return [];
  const { rows } = await inboxRequest<{ rows: SupabaseExportLead[] }>(
    '/inbox/export/leads',
    { accountName },
  );
  return (rows || []).map(supabaseLeadToExportRecord);
}
